import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminListMessages, adminDeleteMessage, api, API } from "@/lib/api";
import { Loader2, Download, LogOut, RefreshCw, Copy, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "mls_admin_key";

export default function AdminMessages() {
  const [key, setKey] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [pending, setPending] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = async (k = key) => {
    if (!k) return;
    setLoading(true);
    try {
      const data = await adminListMessages(k);
      setRows(data);
      setAuthed(true);
    } catch (e) {
      setAuthed(false);
      toast.error(e?.response?.data?.detail || "Access denied");
      localStorage.removeItem(STORAGE_KEY);
      setKey("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
  }, []);

  const onSubmitKey = (e) => {
    e.preventDefault();
    const k = pending.trim();
    if (!k) return;
    localStorage.setItem(STORAGE_KEY, k);
    setKey(k);
    setPending("");
    load(k);
  };

  const onLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setKey("");
    setAuthed(false);
    setRows([]);
  };

  const onDownload = async () => {
    try {
      const r = await api.get("/admin/messages.csv", {
        headers: { "X-Admin-Key": key },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `contact-messages-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch {
      toast.error("Download failed");
    }
  };

  const onCopyEmails = () => {
    const list = [...new Set(rows.map((r) => r.email))].join(", ");
    navigator.clipboard.writeText(list);
    toast.success(`Copied ${new Set(rows.map((r) => r.email)).size} unique emails`);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this message permanently?")) return;
    try {
      await adminDeleteMessage(id, key);
      setRows((prev) => prev.filter((r) => r.id !== id));
      toast.success("Message deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onCopyMessage = (r) => {
    const body = `From: ${r.name} <${r.email}>\nDate: ${new Date(r.created_at).toLocaleString()}\n\n${r.message}`;
    navigator.clipboard.writeText(body);
    toast.success("Message copied to clipboard.");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED] grain flex items-center justify-center px-4">
        <form onSubmit={onSubmitKey} className="w-full max-w-md border border-[#222] p-8" data-testid="admin-msg-login">
          <div className="uppercase-mono text-[#8a8a8a]">Admin access</div>
          <h1 className="font-serif text-3xl mt-2">Enter admin key</h1>
          <p className="text-sm text-[#8a8a8a] mt-3 leading-relaxed">
            Same key as the waitlist admin. Paste it below to browse contact submissions.
          </p>
          <input
            type="password"
            value={pending}
            onChange={(e) => setPending(e.target.value)}
            placeholder="admin key"
            className="w-full px-3 py-3 mt-6 font-mono text-sm"
            data-testid="admin-msg-key-input"
            autoFocus
          />
          <button
            type="submit"
            className="btn-accent w-full mt-4 py-3 uppercase tracking-widest text-[12px]"
            data-testid="admin-msg-key-submit"
          >
            unlock
          </button>
          <div className="flex items-center justify-between mt-6 text-[11px]">
            <Link to="/" className="uppercase-mono text-[#555] hover:text-[#CCFF00]">
              ← back to landing
            </Link>
            <Link to="/admin/waitlist" className="uppercase-mono text-[#555] hover:text-[#CCFF00]">
              waitlist admin →
            </Link>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED] grain">
      {/* Top bar */}
      <header className="border-b border-[#222] px-6 lg:px-12 py-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase-mono text-[#8a8a8a] flex items-center gap-3">
            <span>Admin · contact messages</span>
            <Link to="/admin/waitlist" className="text-[#555] hover:text-[#CCFF00]">
              → waitlist
            </Link>
          </div>
          <h1 className="font-serif text-3xl lg:text-4xl mt-1">
            {rows.length} <span className="text-[#8a8a8a]">message{rows.length === 1 ? "" : "s"}</span>
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => load()}
            data-testid="admin-msg-refresh-btn"
            className="btn-ghost px-3 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2"
          >
            <RefreshCw size={12} strokeWidth={1.5} /> refresh
          </button>
          <button
            onClick={onCopyEmails}
            disabled={!rows.length}
            data-testid="admin-msg-copy-emails-btn"
            className="btn-ghost px-3 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Copy size={12} strokeWidth={1.5} /> copy emails
          </button>
          <button
            onClick={onDownload}
            disabled={!rows.length}
            data-testid="admin-msg-csv-btn"
            className="btn-accent px-4 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Download size={12} strokeWidth={1.5} /> export csv
          </button>
          <button
            onClick={onLogout}
            data-testid="admin-msg-logout-btn"
            className="uppercase-mono text-[#8a8a8a] hover:text-[#FF3333] inline-flex items-center gap-1 ml-2"
          >
            <LogOut size={12} strokeWidth={1.5} /> logout
          </button>
        </div>
      </header>

      {/* List */}
      <section className="px-6 lg:px-12 py-8">
        {loading ? (
          <div className="text-[#8a8a8a] flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" /> loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-[#222] p-16 text-center" data-testid="admin-msg-empty">
            <div className="font-serif text-3xl text-[#8a8a8a]">No messages yet.</div>
            <div className="uppercase-mono text-[#555] mt-2">
              Share <span className="text-[#CCFF00]">/welcome#contact</span> to start collecting
            </div>
          </div>
        ) : (
          <ul className="border border-[#222] divide-y divide-[#222]" data-testid="admin-msg-list">
            {rows.map((r, i) => {
              const expanded = expandedId === r.id;
              return (
                <li key={r.id} data-testid={`admin-msg-row-${i}`} className="bg-[#0A0A0A]">
                  <button
                    onClick={() => setExpandedId(expanded ? null : r.id)}
                    className="w-full grid grid-cols-12 items-center gap-4 px-4 lg:px-6 py-4 text-left hover:bg-[#101010] transition-colors"
                    data-testid={`admin-msg-toggle-${i}`}
                  >
                    <div className="col-span-1 text-[#555] font-mono">
                      {String(rows.length - i).padStart(3, "0")}
                    </div>
                    <div className="col-span-12 md:col-span-3 min-w-0">
                      <div className="font-serif text-lg leading-tight truncate">{r.name}</div>
                      <a
                        href={`mailto:${r.email}?subject=Re%3A%20your%20note%20via%20My%20Lead%20Studio`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-mono text-[12px] text-[#CCFF00] hover:underline truncate block"
                      >
                        {r.email}
                      </a>
                    </div>
                    <div className="col-span-12 md:col-span-5 text-sm text-[#8a8a8a] truncate">
                      {r.message}
                    </div>
                    <div className="col-span-8 md:col-span-2 uppercase-mono text-[#555] font-mono whitespace-nowrap">
                      {new Date(r.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" })}
                    </div>
                    <div className="col-span-4 md:col-span-1 flex justify-end text-[#8a8a8a]">
                      {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                  </button>

                  {expanded && (
                    <div className="px-4 lg:px-6 pb-6 pt-2 grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid={`admin-msg-detail-${i}`}>
                      <div className="lg:col-span-9">
                        <div className="uppercase-mono text-[#8a8a8a] text-[11px] mb-2">Message</div>
                        <div className="whitespace-pre-wrap text-sm leading-relaxed text-[#ededed] border-l-2 border-[#CCFF00] pl-4">
                          {r.message}
                        </div>
                      </div>
                      <div className="lg:col-span-3 space-y-3 text-[11px] font-mono">
                        <div>
                          <div className="uppercase-mono text-[#8a8a8a]">received</div>
                          <div className="text-[#ededed] mt-1">{new Date(r.created_at).toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="uppercase-mono text-[#8a8a8a]">referrer</div>
                          <div className="text-[#ededed] mt-1 truncate">{r.referrer || "direct"}</div>
                        </div>
                        <div className="flex flex-col gap-2 pt-2">
                          <a
                            href={`mailto:${r.email}?subject=Re%3A%20your%20note%20via%20My%20Lead%20Studio&body=${encodeURIComponent(`Hi ${r.name.split(" ")[0]},\n\n`)}`}
                            data-testid={`admin-msg-reply-${i}`}
                            className="btn-accent px-3 py-2 uppercase tracking-widest text-[10px] inline-flex items-center gap-2 justify-center"
                          >
                            reply
                          </a>
                          <button
                            onClick={() => onCopyMessage(r)}
                            data-testid={`admin-msg-copy-${i}`}
                            className="btn-ghost px-3 py-2 uppercase tracking-widest text-[10px] inline-flex items-center gap-2 justify-center"
                          >
                            <Copy size={11} /> copy
                          </button>
                          <button
                            onClick={() => onDelete(r.id)}
                            data-testid={`admin-msg-delete-${i}`}
                            className="uppercase tracking-widest text-[10px] inline-flex items-center gap-2 justify-center text-[#8a8a8a] hover:text-[#FF3333] py-2"
                          >
                            <Trash2 size={11} /> delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <footer className="px-6 lg:px-12 py-6 border-t border-[#222] uppercase-mono text-[#555]">
        Messages are also delivered to your Gmail as they arrive · endpoint: {API}/admin/messages
      </footer>
    </div>
  );
}
